"use client";

import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { ArrowRight, ChevronDown, ChevronLeft } from "lucide-react";
import { useAuthContext } from "@core/helpers/client/contexts/AuthContext";
import { openAuthModal } from "@/hooks/useAuthModal";
import UserDropdown from "@/components/pages/Auth/_userDropdown";
import LoadingSpinner from "@/components/LoadingSpinner";
import useSafeBack from "@core/helpers/client/hooks/useSafeBack";
import { useMenusContext } from "@core/helpers/client/contexts/MenusContext";
import useAnchorClick from "@/helpers/anchorScroll";
import {
    ADS_CONTACT_HREF, ADS_FAQ_HREF, ADS_HOME_HREF, ADS_MENU_KEY, ADS_SERVICES_CHANGE_EVENT, ADS_SERVICES_HREF,
    ADS_SERVICES_TOGGLE_EVENT,
} from "@/helpers/ads";
import AdsLogo from "./_adsLogo";
import useScrollDirection from "./useScrollDirection";
import useIsDesktop from "@core/helpers/client/hooks/useIsDesktop";
import useResizeMeasure from "@/hooks/useResizeMeasure";
import useBodyScrollLock from "@core/helpers/client/hooks/useBodyScrollLock";

const NAV_TRANSITION = 500;

const strip_slash = (path = "") => path.replace(/\/+$/, "") || "/";

/** لینک‌های ردیف دوم؛ کلاس‌ها همان لینک‌های نویگیشن مجله است. */
const NAV_LINK_CLASS = "flex items-center gap-1 py-4 text-base leading-6 text-jmt-main transition hover:text-jmt-primary-800";
/** عنوان «خدمات تبلیغاتی» صفحه‌ای ندارد؛ فقط دراپ‌دون را باز می‌کند و نشانگر لینک نمی‌گیرد. */
const NAV_LABEL_CLASS = "flex cursor-default select-none items-center gap-1 py-4 text-base leading-6 text-jmt-main transition hover:text-jmt-primary-800";

/**
 * «خدمات تبلیغاتی»: دراپ‌دون هاور دقیقا مثل منوی مجله (`_navBlog.jsx`): باز شدن با هاور،
 * کارت سفید با radius و سایه، آیتم‌های `py-3 px-8` و انیمیشن scale/translate.
 * آیتم‌ها از پنل (منوها ← header-main-menu-ads) می‌آید. خود عنوان فقط دسته‌بندی است: لینک نیست
 * و کلیک ندارد؛ با هاور یا فوکوس کیبورد دراپ‌دون باز می‌شود.
 */
function ServicesMenu() {
    const [open, setOpen] = useState(false);
    const { menus } = useMenusContext();
    const items = menus?.[ADS_MENU_KEY] || [];
    const has_items = items.length > 0;

    return (
        <div
            className="relative"
            onMouseEnter={() => has_items && setOpen(true)}
            onMouseLeave={() => setOpen(false)}
            onFocus={() => has_items && setOpen(true)}
            onBlur={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false);
            }}>
            <span
                tabIndex={has_items ? 0 : undefined}
                aria-haspopup={has_items ? "true" : undefined}
                aria-expanded={has_items ? open : undefined}
                className={NAV_LABEL_CLASS}>
                خدمات تبلیغاتی
                {has_items && <ChevronDown size={22} aria-hidden="true" />}
            </span>

            {has_items && (
                <div
                    className={`absolute top-full right-0 z-50 origin-top-right overflow-hidden rounded-md bg-jmt-white shadow-full-light transition-all duration-300 ease-in-out ${open
                        ? "visible translate-y-0 scale-100 opacity-100"
                        : "invisible -translate-y-2 scale-95 opacity-0"
                        }`}>
                    <ul className="flex flex-col items-center justify-center">
                        {items.map((item) => (
                            <li key={item?.id || item?.link?.url}>
                                <Link
                                    href={item?.link?.url || ""}
                                    onClick={() => setOpen(false)}
                                    className="block whitespace-nowrap rounded-md px-8 py-3 text-sm text-jmt-main transition hover:text-jmt-primary-800">
                                    {item?.title}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}

/** دکمه‌ی «ورود | ثبت‌نام» طرح Ads: بدون آیکون، h-40 و حاشیه‌ی neutral-200. */
function AuthButton() {
    const { is_auth, user_loading } = useAuthContext();

    if (user_loading) return <LoadingSpinner size={20} />;
    if (is_auth) return <UserDropdown />;

    return (
        <button
            type="button"
            onClick={() => openAuthModal()}
            className="flex h-10 cursor-pointer items-center justify-center whitespace-nowrap rounded border border-jmt-neutral-200 bg-jmt-white px-4 py-2 text-sm font-bold leading-[22px] text-jmt-neutral-800 transition hover:bg-jmt-neutral-100">
            ورود | ثبت‌نام
        </button>
    );
}

/** مودال تمام‌صفحه‌ی خدمات موبایل: بین هدر (۴۸) و نویگیشن پایین؛ آیتم‌ها از منوی پنل. */
function ServicesDrawer({ open, onClose }) {
    const { menus } = useMenusContext();
    const items = menus?.[ADS_MENU_KEY] || [];

    useBodyScrollLock(open);

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-label="خدمات تبلیغاتی"
            aria-hidden={!open}
            style={{ top: "48px", bottom: "var(--footer-nav-height, 0px)" }}
            className={`fixed inset-x-0 z-30 flex flex-col bg-jmt-white transition-all duration-300 ease-in-out lg:hidden ${open ? "translate-y-0 opacity-100 pointer-events-auto" : "translate-y-full opacity-0 pointer-events-none"}`}>
            <ul className="flex-1 overflow-y-auto px-4">
                {items.map((item) => (
                    <li key={item?.id || item?.link?.url}>
                        <Link
                            href={item?.link?.url || ""}
                            onClick={onClose}
                            className="flex h-11 w-full items-center justify-between gap-2 border-b border-jmt-neutral-200 text-sm leading-[22px] text-jmt-main">
                            <span>{item?.title}</span>
                            <ChevronLeft size={20} className="shrink-0 text-jmt-neutral-900" aria-hidden="true" />
                        </Link>
                    </li>
                ))}
            </ul>
        </div>
    );
}

export default function HeaderAds() {
    const goBack = useSafeBack();
    const pathname = strip_slash(usePathname() || "");

    // خانه و لیست خدمات موبایل ریشه‌اند؛ بقیه‌ی صفحه‌ها دکمه‌ی بازگشت می‌گیرند
    const is_root = pathname === strip_slash(ADS_HOME_HREF) || pathname === strip_slash(ADS_SERVICES_HREF);

    // ردیف دوم دسکتاپ مثل هدر مجله با اسکرول به پایین بسته و با اسکرول به بالا باز می‌شود
    const [services_open, set_services_open] = useState(false);

    // با تغییر مسیر مودال بسته می‌شود؛ دکمه‌ی نویگیشن پایین با رویداد window آن را باز و بسته می‌کند
    useEffect(() => {
        set_services_open(false);
    }, [pathname]);

    useEffect(() => {
        const toggle = () => set_services_open((prev) => !prev);
        window.addEventListener(ADS_SERVICES_TOGGLE_EVENT, toggle);
        return () => window.removeEventListener(ADS_SERVICES_TOGGLE_EVENT, toggle);
    }, []);

    useEffect(() => {
        window.dispatchEvent(new CustomEvent(ADS_SERVICES_CHANGE_EVENT, { detail: { open: services_open } }));
    }, [services_open]);

    const showNavbar = useScrollDirection();
    const [navExpanded, setNavExpanded] = useState(true);

    // لینک‌های # روی همین صفحه مثل منوی سلر آکادمی با اسکرول نرم می‌روند
    const handle_anchor_click = useAnchorClick();

    // scroll-mt بخش سوالات متداول از همین متغیر خوانده می‌شود؛ مثل هدر سلر آکادمی
    const headerRef = useRef(null);
    // SSR و hydration هر دو نسخه را دارند؛ بعد از آن فقط نسخه‌ی همان viewport در DOM می‌ماند
    const is_desktop = useIsDesktop();
    // ارتفاع هدر فقط در ResizeObserver خوانده می‌شود (useResizeMeasure)؛ تغییر اندازه‌ی پنجره هم از همین‌جا می‌رسد
    useResizeMeasure(
        () => [headerRef.current],
        () => {
            if (!headerRef.current) return;
            const height = headerRef.current.getBoundingClientRect().height;
            return () => document.documentElement.style.setProperty("--header-height", `${height}px`);
        },
        []
    );

    useEffect(() => {
        if (!showNavbar) {
            setNavExpanded(false);
            return;
        }

        const timeout = setTimeout(() => setNavExpanded(true), NAV_TRANSITION);
        return () => clearTimeout(timeout);
    }, [showNavbar]);

    return (
        <header dir="rtl" className="relative w-full">
            <div ref={headerRef} className="fixed inset-x-0 top-0 z-40 bg-jmt-white">
                {/* mobile */}
                {is_desktop !== true && <div className="shadow-[0_2px_4px_rgba(0,0,0,0.1)] lg:hidden">
                    <div className="grid h-12 grid-cols-[40px_1fr_40px] items-center gap-2 px-4">
                        <div className="flex size-10 items-center justify-center">
                            {!is_root && (
                                <button
                                    type="button"
                                    aria-label="بازگشت"
                                    onClick={() => goBack()}
                                    className="flex size-10 cursor-pointer items-center justify-center text-jmt-neutral-900">
                                    <ArrowRight size={24} />
                                </button>
                            )}
                        </div>
                        <div className="flex justify-center">
                            <AdsLogo />
                        </div>
                        <span />
                    </div>
                </div>}

                {/* mobile: مودال خدمات */}
                {is_desktop !== true && <ServicesDrawer open={services_open} onClose={() => set_services_open(false)} />}

                {/* desktop */}
                {is_desktop !== false && <div className="hidden lg:block">
                    {/* row 1 */}
                    <div className="w-full border-b border-jmt-neutral-100 py-4">
                        <div className="container">
                            <div className="grid w-full grid-cols-[1fr_auto_1fr] items-center gap-4">
                                <Link href="/" className="justify-self-start text-sm font-bold text-jmt-primary-800 transition hover:text-jmt-primary-900">
                                    رفتن به فروشگاه ژومنت
                                </Link>
                                <AdsLogo />
                                <div className="flex items-center justify-end">
                                    <AuthButton />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* row 2 */}
                    <div
                        className={`border-b border-jmt-neutral-100 transition-all duration-500 ease-in-out ${showNavbar ? "max-h-20 opacity-100" : "max-h-0 opacity-0"} ${navExpanded ? "overflow-visible" : "overflow-hidden"}`}>
                        <nav className="flex w-full items-center justify-center gap-6">
                            <ServicesMenu />
                            <Link href={ADS_FAQ_HREF} onClick={handle_anchor_click(ADS_FAQ_HREF)} className={NAV_LINK_CLASS}>سوالات متداول</Link>
                            <Link href={ADS_CONTACT_HREF} className={NAV_LINK_CLASS}>تماس با ما</Link>
                        </nav>
                    </div>
                </div>}
            </div>
        </header>
    );
}
